import { z } from 'zod';
import { isoDate } from './common.js';
import { Change, ImpactSummary, PUBLISH_BASIS_KINDS } from './rules.js';

/**
 * План изменения правил по ссылке (005, US5, T119; specs/005-rules-mcp/contracts/http-api.md,
 * `GET /api/rules/plans/:planId`; FR-035, FR-037, FR-038). Страница «Правила расчёта» открывает план по параметру
 * `plan`: метка «подготовлено ИИ-агентом» с клиентом и временем заморозки, состав плана, влияние при заморозке и, если
 * данные изменились, пересчитанное влияние, состояние плана с причиной. Публикует человек: тот же
 * `POST /api/rules/draft/publish` с полем `planId`. ФИО и адреса документа клиента в ответе нет.
 */

const id = z.number().int().positive();

/** Состояние плана (FR-035): действует, устарел (с причиной), устарел по сроку, опубликован. */
export const PLAN_STATES = ['active', 'stale', 'expired', 'published'] as const;
export const PlanState = z.enum(PLAN_STATES);
export type PlanState = z.infer<typeof PlanState>;

/** Что изменилось после заморозки: черновик, действующие правила, дата начала, сборка, проверочные примеры, срок. */
export const PLAN_STALE_REASONS = ['draft', 'version', 'date', 'build', 'goldens', 'expired'] as const;
export const PlanStaleReason = z.enum(PLAN_STALE_REASONS);
export type PlanStaleReason = z.infer<typeof PlanStaleReason>;

/** Основание, которое предложил агент: вид, «Что решили», вопрос реестра. Номер документа приказа добавляет человек. */
export const PlanBasis = z.object({
  kind: z.enum(PUBLISH_BASIS_KINDS),
  decided: z.string(),
  questionCode: z.string().optional(),
});
export type PlanBasis = z.infer<typeof PlanBasis>;

/** Влияние всего черновика: сохранённое влияние (`previews`) — номер, числа и время расчёта. */
export const PlanImpact = z.object({
  previewId: id,
  summary: ImpactSummary,
  computedAt: z.string().nullable(),
});
export type PlanImpact = z.infer<typeof PlanImpact>;

/**
 * Влияние сейчас — только у действующего плана:
 * - `same` — данные не менялись, числа заморозки действуют;
 * - `fresh` — данные изменились, есть свежее влияние черновика с датой начала плана: его числа — рядом с числами
 *   заморозки, публиковать — с его `previewId`;
 * - `recompute` — данные изменились, свежего влияния нет: перед публикацией посчитать влияние заново
 *   (`POST /api/rules/draft/preview` с датой начала плана).
 */
export const PlanImpactNow = z.discriminatedUnion('status', [
  z.object({ status: z.literal('same') }),
  z.object({ status: z.literal('fresh'), ...PlanImpact.shape }),
  z.object({ status: z.literal('recompute') }),
]);
export type PlanImpactNow = z.infer<typeof PlanImpactNow>;

/** Ответ `GET /api/rules/plans/:planId`. */
export const PlanResponse = z.object({
  planId: id,
  state: PlanState,
  /** У `stale` и `expired`. */
  reason: PlanStaleReason.optional(),
  /** Текст состояния для человека (у всех, кроме `active`): «План устарел: черновик изменился после заморозки…». */
  stateText: z.string().optional(),
  /** У `published`: опубликованная версия. */
  publishedVersion: z.object({ id, number: z.number().int().positive() }).optional(),
  /** «Подготовлено ИИ-агентом»: название клиента агента и время заморозки. */
  preparedBy: z.object({ client: z.string(), frozenAt: z.string() }),
  expiresAt: z.string(),
  /** Дата начала плана: публикация по плану — только с ней. */
  validFrom: isoDate,
  draftId: id,
  fingerprint: z.string(),
  link: z.string(),
  /** Основание, которое предложил агент. */
  basis: PlanBasis,
  /** Что изменится — изменения словами на момент заморозки. */
  changes: z.array(Change),
  impact: z.object({ frozen: PlanImpact, now: PlanImpactNow.optional() }),
  /** Примеры, у которых меняется результат: человек подтверждает «Так и должно быть». Отметки — на момент заморозки. */
  goldensToConfirm: z.array(
    z.object({ goldenId: id, code: z.string(), title: z.string().nullable(), confirmed: z.boolean() }),
  ),
  /** Риски: человек отмечает «Понимаю риск». Отметки — на момент заморозки. */
  risksToAccept: z.array(
    z.object({ code: z.string(), text: z.string(), fingerprint: z.string(), acked: z.boolean() }),
  ),
  /** Находки «предупреждение» проверки правил. */
  warnings: z.array(z.object({ code: z.string(), text: z.string(), group: z.string().optional() })),
  /** Опубликовать по плану может этот пользователь: он ведёт черновик плана, и план действует. Иначе — только чтение. */
  canPublish: z.boolean(),
});
export type PlanResponse = z.infer<typeof PlanResponse>;

/**
 * Строка списка планов (005, FR-065; `GET /api/rules/plans`): страница «Правила расчёта» показывает планы агентов без
 * ссылки — вкладка «Планы агентов» и строка о действующем плане в полосе черновика. Без ФИО и адреса документа клиента.
 */
export const PlanListItem = z.object({
  planId: id,
  state: PlanState,
  reason: PlanStaleReason.optional(),
  /** Текст состояния для человека (у всех, кроме `active`), как у плана по ссылке. */
  stateText: z.string().optional(),
  publishedVersion: z.object({ id, number: z.number().int().positive() }).optional(),
  preparedBy: z.object({ client: z.string(), frozenAt: z.string() }),
  expiresAt: z.string(),
  validFrom: isoDate,
  draftId: id,
  /** План текущего открытого черновика. */
  currentDraft: z.boolean(),
  /** Изменения словами на момент заморозки. */
  changes: z.array(z.string()),
});
export type PlanListItem = z.infer<typeof PlanListItem>;

/** Ответ `GET /api/rules/plans`: планы объекта, новые сверху, не больше 50. */
export const PlanListResponse = z.object({ plans: z.array(PlanListItem) });
export type PlanListResponse = z.infer<typeof PlanListResponse>;
